import {
  SUPER_COPY_REVIEW_CONFIDENCE_THRESHOLD,
  isSmartFillSemanticKey,
  type SuperCopyReviewClient,
  type SuperCopyReviewDraft,
  type SuperCopyReviewGroup,
  type SuperCopyReviewState,
  type SuperCopyReviewValue,
  type SuperCopySavePayload
} from '@oysterjs/smartfill-contracts';
import { slugName } from '../buckets/edits';

export interface DraftValidation {
  messages: string[];
  invalid: Set<string>;
  conflicts: Set<string>;
}

export function validateDraft(draft: SuperCopyReviewDraft): DraftValidation {
  const messages: string[] = [];
  const invalid = new Set<string>();
  const conflicts = new Set<string>();
  if (draft.clients.length === 0) messages.push('No clients are available to save.');
  for (const client of draft.clients) {
    if (!client.name.trim()) {
      invalid.add(`client:${client.id}`);
      messages.push('Every bucket needs a name.');
    }
    if (client.groups.length === 0) messages.push(`${client.name || 'A client'} needs a group.`);
    let mapped = 0;
    for (const group of client.groups) {
      if (!group.label.trim() || (group.values.length === 0 && !group.entity)) {
        invalid.add(`group:${client.id}:${group.id}`);
        messages.push('Every group needs a name and at least one field.');
      }
      // Each group becomes one entity in the saved bucket, so a field name
      // only needs to be unique within its group.
      const firstByKey = new Map<string, string>();
      for (const value of group.values) {
        const marker = `value:${client.id}:${group.id}:${value.id}`;
        if (!value.label.trim() || !isSmartFillSemanticKey(value.semanticKey)) {
          invalid.add(marker);
          messages.push('Every field needs a name.');
        }
        if (value.semanticKey !== 'unknown') {
          mapped += 1;
          if (!value.value.trim()) {
            invalid.add(marker);
            messages.push('Named fields need a value.');
          }
          const first = firstByKey.get(value.semanticKey);
          if (first) {
            conflicts.add(first);
            conflicts.add(marker);
            messages.push('A field name can be used only once per group.');
          } else {
            firstByKey.set(value.semanticKey, marker);
          }
        }
        if (value.confidence < SUPER_COPY_REVIEW_CONFIDENCE_THRESHOLD && !value.accepted) {
          invalid.add(marker);
          messages.push('Low-confidence values must be reviewed and accepted.');
        }
      }
    }
    if (mapped === 0) messages.push(`${client.name || 'A client'} needs one mapped field.`);
  }
  return { messages: [...new Set(messages)], invalid, conflicts };
}

export function toSavePayload(draft: SuperCopyReviewDraft): SuperCopySavePayload {
  return {
    draftId: draft.draftId,
    visibility: draft.visibility,
    clients: draft.clients.map((client) => ({
      id: client.id,
      name: client.name,
      groups: client.groups.map((group) => ({
        id: group.id,
        kind: group.kind,
        label: group.label,
        ...(group.entity ? { entity: group.entity } : {}),
        values: group.values.map((value) => ({
          id: value.id,
          semanticKey: value.semanticKey,
          label: value.label,
          value: value.value,
          reviewState: value.reviewState,
          accepted: value.accepted
        }))
      }))
    }))
  };
}

export function updateClient(
  draft: SuperCopyReviewDraft,
  client: SuperCopyReviewClient
): SuperCopyReviewDraft {
  return {
    ...draft,
    clients: draft.clients.map((candidate) => (candidate.id === client.id ? client : candidate))
  };
}

/** Removing a record also removes the facts explicitly owned by that record. */
export function removeGroup(client: SuperCopyReviewClient, groupId: string): SuperCopyReviewClient {
  const removed = new Set([groupId]);
  for (let previousSize = 0; previousSize !== removed.size; ) {
    previousSize = removed.size;
    for (const group of client.groups) {
      if (group.entity?.parentGroupId && removed.has(group.entity.parentGroupId))
        removed.add(group.id);
    }
  }
  return { ...client, groups: client.groups.filter((group) => !removed.has(group.id)) };
}

export function newGroup(): SuperCopyReviewGroup {
  return {
    id: `scg-local-${crypto.randomUUID()}`,
    kind: 'CUSTOM',
    label: 'Additional data',
    values: [newValue()]
  };
}

export function newValue(): SuperCopyReviewValue {
  return {
    id: `scv-local-${crypto.randomUUID()}`,
    semanticKey: 'custom.new_field',
    label: 'New field',
    extractedValue: '',
    value: '',
    confidence: 1,
    reviewState: 'CUSTOM',
    accepted: true
  };
}

export function reviewedState(semanticKey: string, edited: boolean): SuperCopyReviewState {
  if (semanticKey === 'unknown') return 'UNKNOWN';
  if (semanticKey.startsWith('custom.')) return 'CUSTOM';
  return edited ? 'EDITED' : 'ACCEPTED';
}

/**
 * Extracted keys are provenance from the extraction model and survive label
 * edits. Unknown and custom values instead take their machine identity from
 * the human label, so renaming "New field" to "Payroll" names the saved
 * property payroll.
 */
export function semanticKeyForLabel(currentKey: string, label: string): string {
  if (currentKey !== 'unknown' && !currentKey.startsWith('custom.')) return currentKey;
  const slug = slugName(label);
  return slug ? `custom.${slug}` : 'unknown';
}
